import { Policy, State } from "@bluefalcon/clients-management-client-fetch";
import { useEffect, useMemo, useState } from "react";
import {
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalCloseButton,
    ModalBody,
    ModalFooter,
    Button,
    FormControl,
    FormLabel,
    Table,
    Th,
    Tr,
    Thead,
    Tbody,
    Td
} from "@chakra-ui/react";
import { LoadingSpinner } from "../components/base/loading-spinner/loading-spinner.comp";
import { SearchableDropdown } from "../components/base/searchable-dropdown/searchable-dropdown.comp";
import { SearchableDropdownCheckboxes } from "../components/base/searchable-dropdown/searchable-dropdown-checkboxes.comp";

type AddPolicyCountyModalProps = {
    policy: Policy;
    isOpen: boolean;
    handleAddCounties: (data: number[]) => Promise<void>;
    closeModal: () => void;
    states: State[];
};

export const AddPolicyCountyModal = ({
    policy,
    isOpen,
    handleAddCounties,
    closeModal,
    states,
}: AddPolicyCountyModalProps) => {
    const [isLoading, setIsLoading] = useState(false);
    const [selectedState, setSelectedState] = useState<State>();
    const [selectedCounties, setSelectedCounties] = useState<{ stateName: string, id: number, countyName: string }[]>(
        policy.states?.flatMap((state) => state.counties.map((c) => ({
            id: c.id,
            countyName: c.value,
            stateName: state.name,
        }))) ?? [],
    );

    const selectedStateCounties = useMemo(() => selectedState?.counties ?? [], [selectedState]);
    const allStateCounties = useMemo(() => selectedState ? selectedState.counties.map((county) => ({
        name: county.value,
        checked: selectedCounties.map((sc) => sc.id).includes(county.id),
    })) : [], [selectedCounties, selectedState]);

    const handleSubmit = async () => {
        try {
            setIsLoading(true);
            if (selectedCounties.length) {
                await handleAddCounties(selectedCounties.map(({ id }) => id));
            }
        } catch (error) { } finally {
            setIsLoading(false);
            onCloseModal();
        }
    };

    const handleRemoveFromList = (id: number) => {
        setSelectedCounties((prevData) => prevData.filter((d) => d.id !== id));
    };

    const onSelectState = (stateName?: string) => {
        setSelectedState(states.find((st) => st.name === stateName));
    };

    const onSelectCounties = (selected: { checked: boolean, name: string }[]) => {
        if (selectedState) {
            setSelectedCounties(selected.filter((fil) => fil.checked).map((sel) => {
                const countyId = selectedState.counties.find((c) => c.value === sel.name)!.id;
                return {
                    id: countyId,
                    countyName: sel.name,
                    stateName: selectedState.name,
                };
            }));
        }
    };

    const onCloseModal = () => {
        setSelectedCounties([]);
        setSelectedState(undefined);
        closeModal();
    };

    return (
        <Modal isOpen={isOpen} onClose={onCloseModal}>
            <ModalOverlay />
            <ModalContent>
                <ModalHeader>{"Add Counties"}</ModalHeader>
                <ModalCloseButton />
                {!isLoading ? <ModalBody>
                    <FormControl mb={4}>
                        <FormLabel htmlFor="state">State</FormLabel>
                        <SearchableDropdown
                            value={selectedState?.name}
                            items={states.map((st) => st.name)}
                            placeholder="Search State"
                            onSelect={onSelectState}
                        />
                    </FormControl>
                    <FormControl mb={4}>
                        <FormLabel htmlFor="county">County</FormLabel>
                        <SearchableDropdownCheckboxes
                            initialItems={allStateCounties}
                            placeholder="Search Counties"
                            onSelect={onSelectCounties}
                            isDisabled={!selectedStateCounties.length}
                        />
                    </FormControl>
                    <Table>
                        <Thead>
                            <Tr>
                                <Th>State</Th>
                                <Th>County</Th>
                                <Th>Action</Th>
                            </Tr>
                        </Thead>
                        <Tbody>
                            {selectedCounties.map((sc) => (
                                <Tr key={sc.id}>
                                    <Td>{sc.stateName}</Td>
                                    <Td>{sc.countyName}</Td>
                                    <Td>
                                        <Button variant={'ghost'} colorScheme="danger" onClick={() => handleRemoveFromList(sc.id)}>
                                            Remove
                                        </Button>
                                    </Td>
                                </Tr>
                            ))}
                        </Tbody>
                    </Table>
                </ModalBody> : <LoadingSpinner />}

                <ModalFooter>
                    <Button isDisabled={isLoading} variant="outline" onClick={onCloseModal}>
                        Close
                    </Button>
                    <Button isDisabled={!selectedCounties.length} colorScheme="teal" ml={3} onClick={handleSubmit}>
                        {"Add Counties"}
                    </Button>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
};
